import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Wrench, Plus, Pencil, Trash2, User, ArrowLeftCircle, History, ShieldAlert, PackageOpen, HardHat, Filter } from "lucide-react";

const CATEGORIES = [
  { value: "strumento", label: "Strumento", icon: Wrench, color: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "dpi", label: "DPI", icon: HardHat, color: "bg-orange-50 text-orange-700 border-orange-200" },
  { value: "altro", label: "Altro", icon: PackageOpen, color: "bg-slate-50 text-slate-700 border-slate-200" },
];

const STATUSES = [
  { value: "warehouse", label: "In magazzino", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "assigned", label: "Assegnata", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { value: "repair", label: "In riparazione", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  { value: "lost", label: "Persa", cls: "bg-red-50 text-red-700 border-red-200" },
  { value: "dismissed", label: "Dismessa", cls: "bg-slate-100 text-slate-600 border-slate-300" },
];

const emptyForm = { category: "strumento", name: "", brand: "", model: "", serial: "", purchase_date: "", purchase_price: "", notes: "", quantity: 1, serial_prefix: "" };

export default function AttrezzaturaPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtri
  const [fltCategory, setFltCategory] = useState("__all__");
  const [fltStatus, setFltStatus] = useState("__all__");
  const [fltTech, setFltTech] = useState("__all__");
  const [fltQuery, setFltQuery] = useState("");

  // Modale create/edit
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Modale assegnazione
  const [assignFor, setAssignFor] = useState(null); // equipment obj
  const [assignTech, setAssignTech] = useState("");
  const [assignNote, setAssignNote] = useState("");

  // Modale cambio stato
  const [statusFor, setStatusFor] = useState(null); // equipment obj
  const [statusValue, setStatusValue] = useState("repair");
  const [statusNote, setStatusNote] = useState("");

  // Storico movimenti
  const [historyFor, setHistoryFor] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [eq, tk] = await Promise.all([
        api.get("/equipment"),
        api.get("/users", { params: { role: "tecnico,subappalto" } }),
      ]);
      setItems(eq.data);
      setTechnicians(tk.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = fltQuery.trim().toLowerCase();
    return items.filter((it) => {
      if (fltCategory !== "__all__" && it.category !== fltCategory) return false;
      if (fltStatus !== "__all__" && it.status !== fltStatus) return false;
      if (fltTech !== "__all__" && (it.technician_id || "") !== fltTech) return false;
      if (q) {
        const hay = `${it.name} ${it.brand} ${it.model} ${it.serial} ${it.technician_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, fltCategory, fltStatus, fltTech, fltQuery]);

  const stats = useMemo(() => {
    const s = { total: items.length, warehouse: 0, assigned: 0, repair: 0, lost: 0 };
    items.forEach((i) => { if (i.status in s) s[i.status] += 1; });
    return s;
  }, [items]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDlgOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      category: it.category || "strumento",
      name: it.name || "",
      brand: it.brand || "",
      model: it.model || "",
      serial: it.serial || "",
      purchase_date: it.purchase_date || "",
      purchase_price: it.purchase_price != null ? String(it.purchase_price) : "",
      notes: it.notes || "",
    });
    setDlgOpen(true);
  };

  const saveForm = async () => {
    if (!form.name.trim()) return toast.error("Nome richiesto");
    const qty = Math.max(1, parseInt(form.quantity) || 1);
    // Modalità bulk (solo in creazione, quantità > 1)
    if (!editing && qty > 1) {
      if (qty > 500) return toast.error("Quantità massima: 500");
      try {
        const payload = {
          base: {
            category: form.category,
            name: form.name.trim(),
            brand: form.brand || "",
            model: form.model || "",
            serial: "",
            purchase_date: form.purchase_date || null,
            purchase_price: Number(form.purchase_price) || 0,
            notes: form.notes || "",
          },
          quantity: qty,
          serial_prefix: form.serial_prefix || "",
        };
        const { data } = await api.post("/equipment/bulk", payload);
        toast.success(`Create ${data.created} attrezzature`);
        setDlgOpen(false);
        load();
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      return;
    }
    // Modalità normale (creazione singola o edit)
    const payload = {
      category: form.category,
      name: form.name.trim(),
      brand: form.brand || "",
      model: form.model || "",
      serial: form.serial || "",
      purchase_price: Number(form.purchase_price) || 0,
      purchase_date: form.purchase_date || null,
      notes: form.notes || "",
    };
    try {
      if (editing) {
        await api.patch(`/equipment/${editing.id}`, payload);
        toast.success("Attrezzatura aggiornata");
      } else {
        await api.post("/equipment", payload);
        toast.success("Attrezzatura creata");
      }
      setDlgOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/equipment/${id}`);
      toast.success("Eliminata");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openAssign = (it) => {
    setAssignFor(it);
    setAssignTech(it.technician_id || "");
    setAssignNote("");
  };
  const doAssign = async () => {
    if (!assignTech) return toast.error(t("auto.seleziona_un_tecnico"));
    try {
      await api.post(`/equipment/${assignFor.id}/assign`, { technician_id: assignTech, note: assignNote });
      toast.success("Assegnata");
      setAssignFor(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doReturn = async (id) => {
    try {
      await api.post(`/equipment/${id}/return`);
      toast.success(t("auto.rientrata_in_magazzino"));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openStatus = (it) => {
    setStatusFor(it);
    setStatusValue(it.status === "assigned" || it.status === "warehouse" ? "repair" : it.status);
    setStatusNote("");
  };
  const doStatus = async () => {
    try {
      await api.post(`/equipment/${statusFor.id}/status`, { status: statusValue, note: statusNote });
      toast.success("Stato aggiornato");
      setStatusFor(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openHistory = async (it) => {
    setHistoryFor(it);
    try {
      const { data } = await api.get(`/equipment/${it.id}/movements`);
      setHistoryRows(data);
    } catch (e) { toast.error(t("auto.errore_caricamento_storico")); setHistoryRows([]); }
  };

  const catMeta = (c) => CATEGORIES.find((x) => x.value === c) || CATEGORIES[CATEGORIES.length - 1];
  const stMeta = (s) => STATUSES.find((x) => x.value === s) || STATUSES[0];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Beni durevoli</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.equipment")}</h1>
          <div className="mt-1 text-sm text-slate-500">{t("auto.strumenti_di_lavoro_dpi_con_tracciamento_assegnazioni")}</div>
        </div>
        <Button onClick={openCreate} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="btn-new-equipment">
          <Plus className="mr-2 h-4 w-4" /> Nuova Attrezzatura
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatBox label="Totale" value={stats.total} testid="stat-total" />
        <StatBox label="In magazzino" value={stats.warehouse} tone="emerald" testid="stat-warehouse" />
        <StatBox label="Assegnate" value={stats.assigned} tone="blue" testid="stat-assigned" />
        <StatBox label="In riparazione" value={stats.repair} tone="amber" testid="stat-repair" />
        <StatBox label="Perse" value={stats.lost} tone="red" testid="stat-lost" />
      </div>

      {/* Filtri */}
      <div className="rounded-sm border border-slate-200 bg-white p-4">
        <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500">
          <Filter className="h-3 w-3" /> Filtri
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div>
            <Label className="text-xs">Categoria</Label>
            <Select value={fltCategory} onValueChange={setFltCategory}>
              <SelectTrigger className="rounded-sm" data-testid="flt-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("auto.tutte")}</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Stato</Label>
            <Select value={fltStatus} onValueChange={setFltStatus}>
              <SelectTrigger className="rounded-sm" data-testid="flt-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("auto.tutti")}</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t("auto.tecnico")}</Label>
            <Select value={fltTech} onValueChange={setFltTech}>
              <SelectTrigger className="rounded-sm" data-testid="flt-tech"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t("auto.tutti")}</SelectItem>
                {technicians.map((tech) => <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Cerca (nome, marca, seriale)</Label>
            <Input value={fltQuery} onChange={(e) => setFltQuery(e.target.value)} placeholder="OTDR, Milwaukee, S-1234…" className="rounded-sm" data-testid="flt-query" />
          </div>
        </div>
      </div>

      {/* Tabella */}
      <div className="overflow-x-auto rounded-sm border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3">{t("common.category")}</th>
              <th className="p-3">Nome / Marca / Modello</th>
              <th className="p-3">Seriale</th>
              <th className="p-3">{t("common.status")}</th>
              <th className="p-3">Assegnata a</th>
              <th className="p-3">Acquisto</th>
              <th className="p-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">Caricamento…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-400">{t("auto.nessuna_attrezzatura")}</td></tr>
            )}
            {filtered.map((it) => {
              const cat = catMeta(it.category);
              const st = stMeta(it.status);
              const CIcon = cat.icon;
              return (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`eq-row-${it.id}`}>
                  <td className="p-3">
                    <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[11px] font-semibold ${cat.color}`}>
                      <CIcon className="h-3 w-3" /> {cat.label}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="font-semibold text-slate-900">{it.name}</div>
                    <div className="text-xs text-slate-500">{[it.brand, it.model].filter(Boolean).join(" · ") || "—"}</div>
                  </td>
                  <td className="p-3 font-mono text-xs">{it.serial || "—"}</td>
                  <td className="p-3">
                    <span className={`rounded-sm border px-1.5 py-0.5 text-[11px] font-semibold ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="p-3 text-xs">
                    {it.technician_name ? (
                      <span className="inline-flex items-center gap-1 rounded-sm bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-700">
                        <User className="h-3 w-3" /> {it.technician_name}
                      </span>
                    ) : <span className="italic text-slate-400">—</span>}
                  </td>
                  <td className="p-3 text-xs">
                    <div className="font-mono">{it.purchase_date || "—"}</div>
                    {it.purchase_price ? <div className="font-mono text-slate-500">€ {Number(it.purchase_price).toFixed(2)}</div> : null}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      {it.status !== "assigned" && it.status !== "lost" && it.status !== "dismissed" && (
                        <Button size="sm" variant="outline" onClick={() => openAssign(it)} className="h-7 rounded-sm text-xs" data-testid={`eq-assign-${it.id}`}>
                          <User className="mr-1 h-3 w-3" /> {t("auto.assegna")}
                        </Button>
                      )}
                      {it.status === "assigned" && (
                        <Button size="sm" variant="outline" onClick={() => doReturn(it.id)} className="h-7 rounded-sm text-xs" data-testid={`eq-return-${it.id}`}>
                          <ArrowLeftCircle className="mr-1 h-3 w-3" /> Rientro
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => openStatus(it)} className="h-7" title="Cambio stato" data-testid={`eq-status-${it.id}`}>
                        <ShieldAlert className="h-4 w-4 text-amber-700" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openHistory(it)} className="h-7" title={t("auto.storico")} data-testid={`eq-history-${it.id}`}>
                        <History className="h-4 w-4 text-slate-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(it)} className="h-7" title="Modifica" data-testid={`eq-edit-${it.id}`}>
                        <Pencil className="h-4 w-4 text-slate-700" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-7" title={t("auto.elimina")} data-testid={`eq-delete-${it.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare "{it.name}"?</AlertDialogTitle>
                            <AlertDialogDescription>{t("auto.verrà_rimossa_anche_tutta_la_sua_storia_movimenti_operazione")}</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteItem(it.id)} className="bg-red-600 hover:bg-red-700">{t("common.delete")}</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modale creazione/modifica */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica attrezzatura" : "Nuova attrezzatura"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Categoria *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-sm" data-testid="frm-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. OTDR EXFO, Casco antiurto" className="rounded-sm" data-testid="frm-name" />
            </div>
            <div>
              <Label>Marca</Label>
              <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="rounded-sm" data-testid="frm-brand" />
            </div>
            <div>
              <Label>Modello</Label>
              <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="rounded-sm" data-testid="frm-model" />
            </div>
            <div>
              <Label>Seriale / Matricola</Label>
              <Input value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} className="rounded-sm font-mono" data-testid="frm-serial" disabled={!editing && Number(form.quantity) > 1} placeholder={!editing && Number(form.quantity) > 1 ? "(auto-generato)" : ""} />
            </div>
            <div>
              <Label>Data acquisto</Label>
              <Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="rounded-sm" data-testid="frm-date" />
            </div>
            <div>
              <Label>Prezzo di acquisto €</Label>
              <Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="rounded-sm font-mono" data-testid="frm-price" />
            </div>
            {!editing && (
              <>
                <div>
                  <Label>Quantità</Label>
                  <Input
                    type="number"
                    min="1"
                    max="500"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    className="rounded-sm font-mono"
                    data-testid="frm-quantity"
                    placeholder="1"
                  />
                  <div className="mt-1 text-[11px] text-slate-500">Duplica lo stesso item N volte (max 500). Modifica i seriali singolarmente dopo la creazione.</div>
                </div>
                {Number(form.quantity) > 1 && (
                  <div>
                    <Label>Prefisso seriale (opz.)</Label>
                    <Input
                      value={form.serial_prefix}
                      onChange={(e) => setForm({ ...form, serial_prefix: e.target.value })}
                      className="rounded-sm font-mono"
                      data-testid="frm-serial-prefix"
                      placeholder="es. GNT- → GNT-001, GNT-002 ..."
                    />
                  </div>
                )}
              </>
            )}
            <div className="col-span-2">
              <Label>{t("common.notes")}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="rounded-sm" data-testid="frm-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDlgOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveForm} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="frm-save">
              {editing ? "Salva" : (Number(form.quantity) > 1 ? `Crea ${form.quantity}` : "Crea")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale assegnazione */}
      <Dialog open={!!assignFor} onOpenChange={(o) => { if (!o) setAssignFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assegna "{assignFor?.name}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tecnico *</Label>
              <Select value={assignTech} onValueChange={setAssignTech}>
                <SelectTrigger className="rounded-sm" data-testid="assign-tech"><SelectValue placeholder={t("auto.scegli_tecnico")} /></SelectTrigger>
                <SelectContent>
                  {technicians.map((tech) => <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("common.notes")}</Label>
              <Textarea value={assignNote} onChange={(e) => setAssignNote(e.target.value)} rows={2} className="rounded-sm" placeholder={t("auto.es_consegnata_per_cantiere_via_roma")} data-testid="assign-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignFor(null)}>{t("common.cancel")}</Button>
            <Button onClick={doAssign} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="assign-confirm">{t("auto.assegna")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale cambio stato */}
      <Dialog open={!!statusFor} onOpenChange={(o) => { if (!o) setStatusFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambio stato "{statusFor?.name}"</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nuovo stato *</Label>
              <Select value={statusValue} onValueChange={setStatusValue}>
                <SelectTrigger className="rounded-sm" data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="warehouse">{t("auto.in_magazzino")}</SelectItem>
                  <SelectItem value="repair">In riparazione</SelectItem>
                  <SelectItem value="lost">Persa</SelectItem>
                  <SelectItem value="dismissed">Dismessa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("common.notes")}</Label>
              <Textarea value={statusNote} onChange={(e) => setStatusNote(e.target.value)} rows={2} className="rounded-sm" placeholder={t("auto.motivo_del_cambio")} data-testid="status-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setStatusFor(null)}>{t("common.cancel")}</Button>
            <Button onClick={doStatus} className="rounded-sm bg-amber-600 hover:bg-amber-700" data-testid="status-confirm">{t("common.confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Storico movimenti */}
      <Dialog open={!!historyFor} onOpenChange={(o) => { if (!o) { setHistoryFor(null); setHistoryRows([]); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-4 w-4" /> Storico di "{historyFor?.name}"</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {historyRows.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400">{t("auto.nessun_movimento")}</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs font-bold uppercase tracking-widest text-slate-500">
                  <tr className="border-b border-slate-200">
                    <th className="p-2 text-left">{t("common.date")}</th>
                    <th className="p-2 text-left">{t("common.type")}</th>
                    <th className="p-2 text-left">Da</th>
                    <th className="p-2 text-left">A</th>
                    <th className="p-2 text-left">{t("common.notes")}</th>
                    <th className="p-2 text-left">Da chi</th>
                  </tr>
                </thead>
                <tbody>
                  {historyRows.map((m) => (
                    <tr key={m.id} className="border-b border-slate-100">
                      <td className="p-2 font-mono text-xs">{(m.date || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="p-2 text-xs"><Badge variant="outline" className="rounded-sm text-[10px] uppercase">{m.type}</Badge></td>
                      <td className="p-2 text-xs">{m.from_technician_name || "—"}</td>
                      <td className="p-2 text-xs">{m.to_technician_name || "—"}</td>
                      <td className="p-2 text-xs text-slate-600">{m.note || "—"}</td>
                      <td className="p-2 text-xs">{m.user_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, tone = "slate", testid }) {
  const toneMap = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
  };
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-1 inline-block rounded-sm px-2 py-1 font-mono text-3xl font-bold ${toneMap[tone]}`}>{value}</div>
    </div>
  );
}
